import { Box, Stack, Typography } from "@mui/material";
import { RailCarLayoutTrack } from "src/store/api/generatedApi";
import {
  AREA_SPACER_LENGTH,
  AreaColorMap,
  AreaHotkeyMap,
  AreaMaterialsMap,
} from "./scrapEyeLayoutCommon";
import { LayoutAreaItem } from "./railCarLayoutItems";
import {
  RailcarConnectorHorizontal,
  RailcarConnectorVertical,
  TrackEndConnectorHorizontal,
  TrackEndConnectorVertical,
} from "./railCarLayoutConnectors";

/**
 * Renders a track for RailCarLayout.
 * Layout orientation controls row vs column rendering.
 */
export const RailCarTrack = ({
  track,
  onSelectArea,
  nonSelectableAreas,
  areaHotkeys,
  areaColors,
  areaMaterials,
  selectedArea,
  orientation,
  isLoadingAreaMaterials,
}: {
  track: RailCarLayoutTrack;
  onSelectArea?: (area: string) => void;
  nonSelectableAreas: readonly string[];
  areaHotkeys?: AreaHotkeyMap;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  selectedArea?: string | null;
  orientation: "row" | "column";
  isLoadingAreaMaterials?: boolean;
}) => {
  const isRailcar = (areaShape: string | undefined) => areaShape === "railcar";
  const firstRailcarIndex = track.areas.findIndex((a) => isRailcar(a.shape));
  const isLastItemRailcar = track.areas.at(-1)?.shape === "railcar";
  const isColumn = orientation === "column";
  const connectorAfter = isColumn ? (
    <RailcarConnectorVertical />
  ) : (
    <RailcarConnectorHorizontal />
  );
  const endConnector = isColumn ? (
    <TrackEndConnectorVertical />
  ) : (
    <TrackEndConnectorHorizontal />
  );
  const spacer = isColumn ? (
    <Box sx={{ height: AREA_SPACER_LENGTH }} />
  ) : (
    <Box sx={{ width: AREA_SPACER_LENGTH }} />
  );

  return (
    <Stack
      direction={isColumn ? "column" : "row"}
      spacing={0}
      alignItems="center"
      justifyContent={isColumn ? "flex-start" : "space-between"}
    >
      <Typography
        variant="caption"
        sx={{
          width: isColumn ? "auto" : 60,
          fontWeight: "bold",
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        {track.name}
      </Typography>
      <Stack
        direction={isColumn ? "column" : "row"}
        spacing={0}
        alignItems="center"
      >
        {firstRailcarIndex !== -1 && endConnector}
        {track.areas.map((area, index) => {
          const currentIsRailcar = isRailcar(area.shape);
          const nextIsRailcar =
            index < track.areas.length - 1 &&
            isRailcar(track.areas[index + 1]?.shape);
          const showConnectorAfter = currentIsRailcar && nextIsRailcar;
          const isLastArea = index === track.areas.length - 1;

          return (
            <Stack
              direction={isColumn ? "column" : "row"}
              alignItems="center"
              key={area.area_id}
              spacing={0}
            >
              <LayoutAreaItem
                area={area}
                onSelectArea={onSelectArea}
                nonSelectableAreas={nonSelectableAreas}
                areaHotkeys={areaHotkeys}
                areaColors={areaColors}
                areaMaterials={areaMaterials}
                selectedArea={selectedArea}
                size="small"
                isLoadingAreaMaterials={isLoadingAreaMaterials}
              />
              {!isLastArea && (showConnectorAfter ? connectorAfter : spacer)}
            </Stack>
          );
        })}
        {isLastItemRailcar ? endConnector : null}
      </Stack>
      {/* Spacer for alignment */}
      {!isColumn && <Box sx={{ width: 60, flexShrink: 0 }} />}
    </Stack>
  );
};
